/**
 * The version segment every consult API path sits under. Call sites are written relative to the
 * base (`${base}/link/exchange`), so this is concatenated here rather than baked into the
 * configured value — see {@link resolveApiBaseUrl} for why that matters.
 */
const API_VERSION_PATH = '/v1';

/**
 * PURE: resolve the API base from a configured base URL.
 *
 * `configuredBase` is a **bare origin** — `https://services.nrxinternal.com`, no version segment —
 * because that is what a `<prefix>-service-url` SSM parameter holds everywhere else on the
 * platform: `NrxApiLambdaService` publishes `this.api.apiEndpoint`, and ffs-signup appends `/v1` in
 * code for exactly that reason. The CI resolver assigns the SSM value VERBATIM and validates only
 * that it is non-empty, so a version segment baked into the parameter would be entirely unguarded —
 * nothing catches a bare origin arriving where a versioned one was assumed, and the symptom is a
 * 404 that this app's own 403/404 → `/index.html` CloudFront rewrite converts into an HTTP 200 with
 * an HTML body, which the client then parses as JSON. Appending here makes the parameter's shape
 * identical to every other `-service-url` and puts the concatenation under test.
 *
 * Split out from {@link getApiBaseUrl} because Vite inlines `import.meta.env` per module at
 * transform time, so a test cannot vary it at runtime — neither `vi.stubEnv` nor mutating the
 * object reaches an already-inlined value. A pure function is what makes every shape assertable.
 *
 * @param configuredBase Bare origin, or undefined/blank for same-origin.
 * @returns Base path or absolute URL, with no trailing slash and exactly one `/v1`.
 *
 * @example
 * ```ts
 * resolveApiBaseUrl(undefined);                          // "/v1"
 * resolveApiBaseUrl('https://services.example.com');     // "https://services.example.com/v1"
 * resolveApiBaseUrl('https://services.example.com/');    // "https://services.example.com/v1"
 * resolveApiBaseUrl('https://services.example.com/v1/'); // "https://services.example.com/v1"
 * ```
 */
export function resolveApiBaseUrl(configuredBase: string | undefined): string {
  const raw = configuredBase?.trim();
  if (!raw) return API_VERSION_PATH;
  // Strips ANY number of trailing slashes AND a version segment the operator baked in, so the
  // bare-origin convention holds even when the published value breaks it. Both shapes are things a
  // human pastes into SSM, and nothing between the parameter and here validates SHAPE — the CI
  // resolver assigns it verbatim and checks only non-emptiness. A doubled `/v1/v1` 404s on every
  // request, and this app's own 403/404 → `/index.html` rewrite turns that 404 into an HTTP 200
  // with an HTML body, which the client then tries to parse as JSON.
  return `${raw.replace(/(?:\/v1)?\/*$/, '')}${API_VERSION_PATH}`;
}

/**
 * Resolves the `/v1` API base for fetch calls, from `VITE_CONSULT_SERVICE_URL` baked in at build time.
 *
 * @remarks Set `VITE_CONSULT_SERVICE_URL` to the service's BARE ORIGIN (no `/v1`); its source in a
 * deployed build is the SSM parameter `l3-apps/consult-scheduling-service-url`, declared as required build env
 * in `service.manifest.json`. Locally omit it to use the relative `/v1` with the Vite dev proxy.
 *
 * @returns Base path or absolute URL without a trailing slash.
 *
 * @example
 * ```ts
 * // Local: returns "/v1"
 * // Deployed: returns "https://services.nrxinternal.com/v1"
 * const base = getApiBaseUrl();
 * ```
 */
export function getApiBaseUrl(): string {
  return resolveApiBaseUrl(import.meta.env.VITE_CONSULT_SERVICE_URL);
}
